import moment from "moment";

export type IFlagsErrorable = IFlags | IGraphEntityError;

export interface IGraphEntityError {
  __typename: "GraphEntityError";
}

export interface IFlags {
  __typename: "Flags";
  flags: string[];
}

export interface IUser {
  createdTime: any;
}

export const FlagNamespaces = {
  ChromeExtension: "ChromeExtension",
  Webapp: "Webapp",
  Services: "Services",
} as const;

type FlagDefinition = {
  name: string;
  description: string;
  unfetchedValue: boolean;
};

type FlagDefinitionHash = {
  [product: string]: {
    [flag: string]: FlagDefinition;
  };
};

export const UserFlags: FlagDefinitionHash = {
  Onboarding: {
    HasInstalledChromeExtension: {
      name: "Has Installed the Chrome Extension",
      description: "A boolean that represents whether the user has installed the Chrome Extension",
      unfetchedValue: true,
    },
    InlineAutopilot: {
      name: "Seen Inline Autopilot Toggle in Google Calendar",
      description:
        "A boolean that determines if a user has seen the inline info tip about the Autopilot Toggle within Google Calendar",
      unfetchedValue: true,
    },
    TeamCalendar: {
      name: "Seen Team Calendar Toggle in Google Calendar",
      description:
        "A boolean that determines if a user has seen the tip about the team calendar in Google Calendar.",
      unfetchedValue: true,
    },
    TeamCalendarSharedEvent: {
      name: "Seen Team Calendar Tutorial in Event View",
      description:
        "A boolean that determines if a user has seen the tip about the team calendar in the Event View.",
      unfetchedValue: true,
    },
    InstallChromeExtension: {
      name: "Seen Install Chrome Extension Callout",
      description:
        "A boolean that determines if a user has seen the callout to install the chrome extension from the Smart Agenda Canvas View",
      unfetchedValue: true,
    },
    TeamHealth: {
      name: "Seen Onboarding TeamHealth",
      description:
        "A boolean that determines if a user has seen the inline info tip on the team health dashboard",
      unfetchedValue: true,
    },
    FeedScheduler: {
      name: "Seen Feed Scheduler",
      description:
        "A boolean that determines if a user has seen the info tip about the feed scheduler",
      unfetchedValue: true,
    },
    ShouldSeeWeekViewTooltips: {
      name: "Should See Week View Tooltips",
      description:
        "A boolean that determines if a user should see week view tooltips in the webapp",
      unfetchedValue: false,
    },
    HasSeenWeekViewAutopilotTooltip: {
      name: "Has Seen Week View Autopilot Tooltip",
      description:
        "A boolean that determines if a user should see the week view autopilot tooltip in the webapp",
      unfetchedValue: false,
    },
    HasSeenWeekViewTeamEventTooltip: {
      name: "Has Seen Week View Team Event Tooltip",
      description:
        "A boolean that determines if a user should see the week view team event tooltip in the webapp",
      unfetchedValue: false,
    },
    HasSeenWeekViewEventDetailsTooltip: {
      name: "Has Seen Week View Event Details Tooltip",
      description:
        "A boolean that determines if a user should see the week view event details tooltip in the webapp",
      unfetchedValue: false,
    },
    HasSeenWeekViewColorCodingTooltip: {
      name: "Has Seen Week View Color Coding Tooltip",
      description:
        "A boolean that determines if a user should see the week view color coding tooltip in the webapp",
      unfetchedValue: false,
    },
    HasSeenWeekViewAutopilotSettingsTooltip: {
      name: "Has Seen Week View Autopilot Settings Tooltip",
      description:
        "A boolean that determines if a user should see the week view autopilot settings tooltip in the webapp",
      unfetchedValue: false,
    },
    HasSeenAllWeekViewTooltips: {
      name: "Has Seen All Week View Tooltips",
      description:
        "A boolean that determined if a user has already seen all of the week view tooltips",
      unfetchedValue: false,
    },
    CustomColorsEverSetOnboardingModal: {
      name: "Custom Colors Ever Set Onboarding Modal",
      description:
        "The backend crawls events to see if a user has ever custom colored an event. if so we want to make sure they are aware that color coding will irreversibly override these",
      unfetchedValue: false,
    },
  },
} as const;

const ValidFlags = Object.values(UserFlags.Onboarding)[0].name; // Pluck a single type here for export
export type Flag = typeof ValidFlags;

export function validateFlagsErrorable(flagsErrorable: IFlagsErrorable): flagsErrorable is IFlags {
  return flagsErrorable && flagsErrorable.__typename !== "GraphEntityError";
}

export function isFlagSet(flagName: string, flagsErrorable: IFlagsErrorable) {
  if (!validateFlagsErrorable(flagsErrorable)) {
    console.error("flags is a GraphEntityError");
    return null;
  }

  return flagsErrorable.flags.indexOf(flagName) > -1;
}

export const getFlagValue = (flagsErrorable: IFlagsErrorable) => (flagName: string) =>
  isFlagSet(flagName, flagsErrorable);

export function isUserAtLeastXDaysOld(user: IUser, days: number) {
  if (!user.createdTime) {
    console.error("user fragment is incomplete");
    return null;
  }

  const now = moment();
  const createdMoment = moment(user.createdTime);

  // + => future, - => past
  const diffInMillis = Math.abs(createdMoment.diff(now));
  const duration = moment.duration(diffInMillis, "ms");

  return duration.asHours() > days * 24;
}
